"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@punchup/ui-common/shadcn/ui/alert-dialog";
import { Button, buttonVariants } from "@punchup/ui-common/shadcn/ui/button";
import { useToast } from "@punchup/ui-common/shadcn/ui/use-toast";
import { HelpCircle, TrashIcon } from "lucide-react";
import { SentryFeatureTags, logException } from "@punchup/sentry";
import { VenuePagesRow, updateVenueInfoTags } from "../../data/venue_pages";
import { cn } from "@punchup/utils/cn";
import { lightenColor } from "@punchup/utils/colors";
import { startTransition, useState } from "react";

import { ICON_MAP } from "./VenueInfoIcons";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { revalidatePathClient } from "@punchup/utils/vercel";
import { useRouter } from "next/navigation";

export interface VenueInfoTag {
  icon: keyof typeof ICON_MAP;
  key: string;
  value: string;
}

function VenueInfoValue({ value }: { value: string }) {
  return (
    <div className="text-xs font-light break-words">
      {value.match(/^(https?:\/\/|www\.)/i) ? (
        <a
          href={value.startsWith("www.") ? `https://${value}` : value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-800 hover:underline"
        >
          {value}
        </a>
      ) : (
        value
      )}
    </div>
  );
}

export interface VenueInfoTagsProps {
  deleteEnabled?: boolean;
  venuePage: VenuePagesRow;
  accentColor?: string;
}

export function VenueInfoTags({
  deleteEnabled,
  venuePage,
  accentColor = "#ff5e26",
}: VenueInfoTagsProps) {
  const { toast } = useToast();
  const router = useRouter();
  const supabase = createClientComponentClient();
  const [tagToDelete, setTagToDelete] = useState<string | null>(null);

  const info_tags = (venuePage.info_tags as unknown as VenueInfoTag[]) ?? [];

  const handleDeleteTag = async () => {
    try {
      if (!tagToDelete) return;

      // Filter out the tag to delete by matching all properties
      const updatedTags = info_tags.filter((tag) => {
        const tagToDeleteObj = info_tags.find((t) => t.key === tagToDelete);
        return !(
          tag.key === tagToDeleteObj?.key &&
          tag.icon === tagToDeleteObj?.icon &&
          tag.value === tagToDeleteObj?.value
        );
      });

      await updateVenueInfoTags({
        supabase,
        venuePageId: venuePage.id,
        newTags: updatedTags,
      });

      toast({
        title: "Success",
        variant: "success",
        description: "Info item deleted successfully",
      });
      await revalidatePathClient(`/v/${venuePage.slug}`);
      startTransition(() => {
        // Refresh the current route and fetch new data from the server without
        // losing client-side browser or React state.
        router.refresh();
      });
    } catch (error) {
      logException(error, {
        extra: {
          venuePageId: venuePage.id,
        },
        tags: {
          feature: SentryFeatureTags.Venues,
        },
      });
      toast({
        title: "Error",
        description: "Failed to delete info item. Please try again.",
        variant: "destructive",
      });
    } finally {
      setTagToDelete(null);
    }
  };

  // Settings page layout (deleteEnabled mode)
  if (deleteEnabled) {
    return (
      <div className="flex flex-col gap-6">
        <div
          className={cn(
            "grid gap-x-2 gap-y-4 md:gap-y-8 grid-cols-1 md:grid-cols-2",
            info_tags.length <= 2 ? "" : "lg:grid-cols-3"
          )}
        >
          {info_tags.map((item, index) => {
            if (
              !item ||
              !("icon" in item) ||
              !("key" in item) ||
              !("value" in item)
            )
              return null;
            const IconComponent = ICON_MAP[item.icon] || HelpCircle;
            const iconName = item.icon in ICON_MAP ? item.icon : "helpCircle";
            return (
              <div key={index} className="relative">
                <div
                  className={cn(
                    "flex flex-row gap-x-2",
                    item.value && item.value.length > 0
                      ? "items-start"
                      : "items-center"
                  )}
                >
                  <div
                    className="p-2 rounded-lg w-fit shrink-0 text-primary"
                    style={{
                      backgroundColor: lightenColor(accentColor, 0.8),
                      color: accentColor,
                    }}
                  >
                    <IconComponent
                      height={20}
                      width={20}
                      data-testid={`icon-${iconName}`}
                    />
                  </div>
                  <div className="flex flex-col h-full min-w-0 text-secondary-blue">
                    <p className="text-sm font-medium">{item.key}</p>
                    {item.value && item.value.length > 0 && (
                      <VenueInfoValue value={item.value} />
                    )}
                  </div>
                  <AlertDialog
                    open={tagToDelete === item.key}
                    onOpenChange={(open) =>
                      setTagToDelete(open ? item.key : null)
                    }
                  >
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        className="absolute top-0 right-0 w-6 h-6 p-0"
                      >
                        <TrashIcon className="w-4 text-destructive" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Are you sure you want to delete this info item?
                        </AlertDialogTitle>
                        <div className="flex flex-col gap-4">
                          <div className="flex items-center gap-x-2">
                            <div className="p-[10px] bg-light-orange rounded-lg w-fit text-primary">
                              <IconComponent height={24} width={24} />
                            </div>
                            <span className="flex flex-col text-secondary-blue">
                              <span className="text-md">{item.key}</span>
                              <VenueInfoValue value={item.value} />
                            </span>
                          </div>
                          <div>
                            <AlertDialogDescription>
                              This action cannot be undone. This will
                              permanently delete the info item.
                            </AlertDialogDescription>
                          </div>
                        </div>
                      </AlertDialogHeader>
                      <AlertDialogFooter className="gap-4 sm:justify-between">
                        <AlertDialogCancel className="w-24 mb-2 sm:mb-0">
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          className={cn(
                            buttonVariants({ variant: "destructive" }),
                            "w-24"
                          )}
                          onClick={(e) => {
                            e.preventDefault();
                            handleDeleteTag();
                          }}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Public venue page layout
  return (
    <div className="flex flex-col gap-y-6">
      <h4 className="text-lg font-bold text-primary-blue">Venue info</h4>
      <div
        className={cn(
          "grid gap-x-2 gap-y-4 md:gap-y-8 grid-cols-1 md:grid-cols-2",
          info_tags.length <= 2 ? "" : "lg:grid-cols-3"
        )}
      >
        {info_tags.map((item, index) => {
          if (
            !item ||
            !("icon" in item) ||
            !("key" in item) ||
            !("value" in item)
          )
            return null;
          const IconComponent = ICON_MAP[item.icon] || HelpCircle;
          const iconName = item.icon in ICON_MAP ? item.icon : "helpCircle";
          return (
            <div key={index} className="relative">
              <div
                className={cn(
                  "flex flex-row gap-x-2",
                  item.value && item.value.length > 0
                    ? "items-start"
                    : "items-center"
                )}
              >
                <div
                  className="p-2 rounded-lg w-fit shrink-0 text-primary"
                  style={{
                    backgroundColor: lightenColor(accentColor, 0.8),
                    color: accentColor,
                  }}
                >
                  <IconComponent
                    height={20}
                    width={20}
                    data-testid={`icon-${iconName}`}
                  />
                </div>
                <div className="flex flex-col h-full min-w-0 text-secondary-blue">
                  <p className="text-sm font-medium">{item.key}</p>
                  {item.value && item.value.length > 0 && (
                    <VenueInfoValue value={item.value} />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
